import axiosNest from '../../api/axiosNest';
import { calendarDate, dateTimeRange } from '../../shared/dateTime';
import { plNumber } from '../../shared/number';

/**
 * Publikacje na giełdach — wywołania API.
 *
 * Odpowiada `apps/api/src/publikacje/publikacje.controller.ts`. Jak przy
 * ofertach na pojazdach: `axiosNest`, nie dataProvider — to decyzje
 * człowieka (wystaw, wycofaj), nie CRUD.
 */

export const EXCHANGE_TIMOCOM = 'timocom';
export const EXCHANGE_TRANSEU = 'transeu';
/** Giełdy, na które sekcja zlecenia pozwala wystawić — kolejność przycisków. */
export const PUBLISHING_EXCHANGES = [EXCHANGE_TIMOCOM, EXCHANGE_TRANSEU];

export const STATUS = {
  PENDING: 'pending',
  POSTED: 'posted',
  FAILED: 'failed',
  WITHDRAWN: 'withdrawn',
  CANCELLED: 'cancelled',
} as const;

/** Jedna forma dla wszystkich stanów (01.10.2026) — jak w API (`posting-statuses.ts`). */
export const POSTING_STATUS_LABELS: Record<string, string> = {
  pending: 'Oczekująca',
  posted: 'Wystawiona',
  failed: 'Nieudana',
  withdrawn: 'Wycofana',
  cancelled: 'Anulowana',
};

export const EXCHANGE_LABELS: Record<string, string> = {
  timocom: 'TIMOCOM',
  transeu: 'Trans.eu',
};

/** Środowisko giełdy z API: wszystko poza tym jest środowiskiem testowym. */
export const ENVIRONMENT_PRODUCTION = 'produkcja';

export const exchangeLabel = (exchange: string) => EXCHANGE_LABELS[exchange] ?? exchange;

/**
 * Komunikaty o dostępie do giełdy (omówienie 01.10.2026). Bez danych
 * dostępowych spółki — jeden komunikat o braku dostępu: ani „piaskownica”,
 * ani nazwy kluczy magazynu poświadczeń. Z danymi i środowiskiem testowym —
 * tryb testowy, nazwany po polsku.
 */
export const noAccessText = (exchange: string) =>
  `Brak dostępu do giełdy ${exchangeLabel(exchange)} — wystawianie nieaktywne do czasu otrzymania konta.`;

export const testModeText = (exchange: string) =>
  `Tryb testowy ${exchangeLabel(exchange)} — oferty nie trafiają na giełdę i przewoźnicy ich nie widzą.`;

/**
 * Wiersz wystawionej publikacji w trybie testowym (07.10.2026): zamiast
 * linku „oferta … na giełdzie”, który udawał prawdziwą publikację. Ten sam
 * sens co wpis w dzienniku zlecenia (API `postedLogText`).
 */
export const TEST_MODE_POSTED_TEXT = 'Wystawiona w trybie testowym — oferta nie jest widoczna na giełdzie.';

/**
 * Czy wiersz wystawiono w środowisku testowym — z wyniku publikacji, nie
 * z bieżącej konfiguracji (wiersz sprzed zmiany środowiska mówi prawdę
 * o sobie). Brak pola (stare wiersze, Trans.eu sprzed 24.09) = nie wiemy,
 * więc nie podpisujemy trybu testowego.
 */
export const postedInTestMode = (result: PublicationOutcome | null | undefined): boolean =>
  typeof result?.environment === 'string' && result.environment !== ENVIRONMENT_PRODUCTION;

/**
 * Braki danych dostępowych spółki: nazwy kluczy magazynu tylko dla
 * administratora (on je wpisuje), pozostali widzą samo „niekompletne”
 * (07.10.2026 — nazwy kluczy technicznych tylko dla administratora).
 */
export const accountStateLabel = (
  account: ExchangeAccounts['companies'][number],
  forAdmin: boolean,
): string => {
  const name = account.nazwa ?? `spółka #${account.companyId}`;
  if (account.complete) return `${name}: komplet`;
  return forAdmin ? `${name}: brakuje ${account.missing.join(', ')}` : `${name}: dane dostępowe niekompletne`;
};

/** Powody automatycznego zdjęcia z giełdy — kody z API (`auto-withdrawal.ts`), te same etykiety. */
export const UNPUBLISH_REASON_LABELS: Record<string, string> = {
  usuniete: 'zlecenie usunięte',
  'obsadzone-flota': 'zlecenie obsadzone flotą własną',
  zamkniete: 'zlecenie zamknięte (np. sprzedane albo nieaktualne)',
  'poza-trade': 'zlecenie wróciło z Trade',
  // Paczka p. Mariusza 21.10, S-21.10-17 — akceptacja przewoźnika.
  'przewoznik-zaakceptowany': 'trader zaakceptował przewoźnika',
  wygasle: 'termin załadunku minął',
};

export interface PublicationOutcome {
  /** Zdanie dla operatora. */
  error?: string;
  /** Szczegóły techniczne błędu (kod HTTP, ścieżka, treść giełdy) — dymek administratora. */
  error_details?: string;
  braki?: string[];
  zalozenia?: string[];
  environment?: string;
  timocom_id?: string;
  delete_from_exchange?: string;
  delete_error?: string;
  delete_error_details?: string;
  auto_withdraw_error_details?: string | null;
  withdrawn_reason?: string;
  poprzedni_blad?: string | null;
  [k: string]: unknown;
}

/**
 * Wariant floty przy zleceniu w Trade (makieta back office v2, fala B,
 * 21.10.2026: BR-TRD-10, TRD-05; decyzja D6 — ostrzeżenie, nie blokada;
 * przełącznik `publicationFleetVariantsWarning`). Z API
 * (`postings/fleet-variants-warning.ts`).
 */
export interface FleetVariantView {
  candidateId: number;
  carId: number;
  vehicle: string;
  addedById: number | null;
  /** Login autora; `null` — wariant bez autora. */
  addedBy: string | null;
  addedAt: string;
}

/** „DEMO 003 (planista), DEMO 006 (autor nieznany)” — jak w API. */
export const fleetVariantsList = (variants: ReadonlyArray<Pick<FleetVariantView, 'vehicle' | 'addedBy'>>) =>
  variants.map((v) => `${v.vehicle} (${v.addedBy ?? 'autor nieznany'})`).join(', ');

/** Zdanie ostrzeżenia — to samo w sekcji, oknie i kolejce (lustro API). */
export const fleetVariantsWarningText = (variants: ReadonlyArray<Pick<FleetVariantView, 'vehicle' | 'addedBy'>>) =>
  `Zlecenie ma warianty floty: ${fleetVariantsList(variants)}. Przed publikacją skonsultuj się z FTL — ostrzeżenie nie blokuje wystawienia.`;

/**
 * Ostrzeżenie zapisane w wyniku publikacji — kto opublikował mimo wariantów
 * floty i które widział. `null`, gdy wiersz go nie ma (bez wariantów albo
 * sprzed zmiany).
 */
export function storedFleetVariantsText(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { variants?: unknown };
  if (!Array.isArray(r.variants) || r.variants.length === 0) return null;
  const variants = r.variants
    .filter((v): v is { vehicle: string; addedBy: string | null } => !!v && typeof (v as { vehicle?: unknown }).vehicle === 'string')
    .map((v) => ({ vehicle: v.vehicle, addedBy: typeof v.addedBy === 'string' ? v.addedBy : null }));
  return variants.length > 0 ? `zlecono mimo ostrzeżenia o wariantach floty: ${fleetVariantsList(variants)}` : null;
}

/**
 * Zatwierdzony wniosek o wyjątek zapisany przy publikacji
 * (`bramki.dopuszczalnosc.wniosek`, fala B 21.10.2026). `null` — bez wniosku.
 */
export function storedExceptionRequestText(result: PublicationOutcome | null | undefined): string | null {
  const gates = result?.bramki as { dopuszczalnosc?: { wniosek?: { id?: unknown } } } | undefined;
  const id = gates?.dopuszczalnosc?.wniosek?.id;
  return typeof id === 'number' ? `wyjątek od zakazu na wniosek nr ${id}` : null;
}

export interface Publication {
  id: number;
  userOfferId: number;
  exchange: string;
  postingNumber: number;
  status: string;
  requestedAt: string;
  postedAt: string | null;
  postedLink: string | null;
  exchangeOfferId: string | null;
  postingResult: PublicationOutcome | null;
  requestedBy: { id: number; username: string } | null;
  /**
   * Warianty floty zlecenia (kolejka publikacji, wiersze oczekujące
   * i wystawione; fala B 21.10.2026). Brak — bez wariantów.
   */
  fleetVariants?: FleetVariantView[];
  userOffer?: {
    id: number;
    externalId: string | null;
    fromCountry: string;
    fromCity: string | null;
    toCountry: string;
    toCity: string | null;
    fromDate: string;
    planningStatus: string;
    companyId: number;
  } | null;
}

/** Reguła dopuszczalności podwykonawcy — kody z API, etykiety po polsku (jak w `postings/gates.ts`). */
export const SUBCONTRACTOR_RULE_LABELS: Record<string, string> = {
  dozwolony: 'podwykonawca dozwolony',
  warunkowy: 'podwykonawca dozwolony warunkowo',
  zakaz: 'zakaz podwykonawstwa',
  nieustalona: 'reguła nieustalona',
};

/**
 * Reguła klienta zdaniem — do sekcji publikacji przy zleceniu (01.10.2026:
 * zamiast „Klient X: podwykonawca warunkowo · lokalizacja publikowana: …”).
 */
export function subcontractorRuleSentence(rule: string, clientName?: string | null): string {
  const name = clientName?.trim();
  const client = name ? `Klient ${name}` : 'Klient';
  switch (rule) {
    case 'dozwolony':
      return `${client} dopuszcza podwykonawców.`;
    case 'warunkowy':
      return `${client} dopuszcza podwykonawców warunkowo.`;
    case 'zakaz':
      return `${client} zakazuje przekazywania zleceń podwykonawcom.`;
    case 'nieustalona':
      return name
        ? `Nie ustalono, czy klient ${name} dopuszcza podwykonawców.`
        : 'Nie ustalono, czy klient dopuszcza podwykonawców.';
    default:
      return `${client}: ${SUBCONTRACTOR_RULE_LABELS[rule] ?? rule}.`;
  }
}

/**
 * Tryby maskowania lokalizacji do wyboru przy publikacji.
 *
 * Bez dokładnego adresu: klient wymaga maskowania kodów na wszystkich
 * zleceniach (odpowiedź Trade 25.09.2026) i API taki tryb odrzuca. Etykieta
 * zostaje, żeby dało się odczytać wcześniejsze publikacje.
 */
export const MASKING_MODES: Array<{ kod: string; label: string }> = [
  { kod: 'kod-skrocony', label: 'skrócony kod pocztowy (miasto i dwie pierwsze cyfry kodu)' },
  { kod: 'tylko-miasto', label: 'tylko miasto (bez kodu pocztowego)' },
];
const LEGACY_MASKING_LABELS: Record<string, string> = {
  dokladna: 'dokładna (miasto i pełny kod pocztowy) — już niedozwolona',
};

/**
 * Tryb „przybliżony” (odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 19:
 * „pełny kod pocztowy lokalizacji leżącej w promieniu 35 km od wskazanej”),
 * za przełącznikiem `maskingNearbyLocality`. Promień jest parametrem
 * z historią, więc etykieta nie podaje liczby.
 */
export const MASKING_MODE_NEARBY = 'przyblizony';
export const NEARBY_MASKING_OPTION = {
  kod: MASKING_MODE_NEARBY,
  label: 'przybliżony (inna miejscowość w pobliżu z pełnym kodem pocztowym — nie faktyczny adres)',
};
/** Tryby do wyboru — „przybliżony” tylko z przełącznikiem. */
export const maskingModesFor = (nearbyOn: boolean): Array<{ kod: string; label: string }> =>
  nearbyOn ? [...MASKING_MODES, NEARBY_MASKING_OPTION] : MASKING_MODES;

/** Brak trybu: „nie ustawiono”, jak pusta opcja listy w kolejce publikacji (01.10.2026). */
export const MASKING_MODE_UNSET = 'nie ustawiono';
export const maskingModeLabel = (code: string | null | undefined) =>
  maskingModesFor(true).find((t) => t.kod === code)?.label ??
  (code && LEGACY_MASKING_LABELS[code]) ??
  MASKING_MODE_UNSET;

/**
 * Konto oferty (odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 18) — z API
 * (`postings/exchange-account.ts`): spółki zlecenia, wspólne innej spółki
 * albo imienne tradera. Bez poświadczeń.
 */
export interface ExchangeAccountView {
  kind: 'company' | 'shared' | 'personal';
  companyId: number;
  userId: number | null;
  fallback?: boolean;
  companyName: string | null;
  /** Zdanie z API, np. „konto wspólne spółki Marathon PL”. */
  label: string;
}

/** „Oferta pójdzie z: …” — zdanie przy przycisku i w oknie publikacji. */
export const accountSentence = (account: ExchangeAccountView | undefined | null): string | null =>
  account ? `Oferta pójdzie z: ${account.label}.` : null;

/** Konto zapisane przy publikacji (wynik publikacji) — krótko, do wiersza kolejki. */
export function storedAccountLabel(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { kind?: unknown; fallback?: unknown };
  const fallback = r.fallback === true ? ' (zapasowo)' : '';
  switch (r.kind) {
    case 'personal':
      return 'konto imienne wystawiającego';
    case 'shared':
      return `konto wspólne innej spółki${fallback}`;
    case 'company':
      return fallback ? `konto spółki${fallback}` : null;
    default:
      return null;
  }
}

export interface PostingGates {
  dopuszczalnosc: {
    regula: string;
    contractor: { id: number; legalName: string } | null;
    requiresConfirmation: boolean;
    requiresException: boolean;
    mayGrantException: boolean;
    /**
     * Typ klienta z TMS zdaniem (16.10.2026, pyt. 38) — podpowiedź z rozdziału
     * Trade, nie reguła; starsze API go nie niesie.
     */
    customerTypeHint?: string | null;
    /**
     * Wniosek o wyjątek (fala B, 21.10.2026, `publicationExceptionRequests`):
     * pole tylko wtedy, gdy pytający może złożyć wniosek.
     */
    mayRequestException?: boolean;
    /** Obowiązujący wniosek (czeka albo zatwierdzony) — pole tylko wtedy, gdy jest. */
    exceptionRequest?: ExceptionRequestView;
  };
  maskowanie: { companyMode: string | null; requiresChoice: boolean };
}

/** Profil pojazdu w ofercie — wspólny dla obu giełd; słownik giełdy zna serwer. */
export interface VehicleProfile {
  body: string;
  /** Metry częściówki „K x LDM”; `null` = całe auto. */
  partialLdm: number | null;
  forklift: boolean;
}

/** Podpowiedź pojazdu z typu zlecenia (serwer: `postings/vehicle-profile.ts`). */
export interface VehicleSuggestion {
  /** `null` = typ zlecenia spoza słownika — trader musi wybrać. */
  profile: VehicleProfile | null;
  source: 'order' | 'default' | null;
  requiredType: string | null;
  /** Skąd podpowiedź — gotowe zdanie dla operatora. */
  note: string;
}

export const VEHICLE_BODY_OPTIONS: Array<{ kod: string; label: string }> = [
  { kod: 'JUMBO', label: 'JUMBO (15,6 m)' },
  { kod: 'MEGA', label: 'MEGA (13,6 m)' },
  { kod: 'STANDARD', label: 'STANDARD (firanka / plandeka 13,6 m)' },
];

/** Najdłuższa częściówka — długość ładowni JUMBO (jak na serwerze). */
export const PARTIAL_LDM_MAX = 15.6;

export const sameVehicle = (a: VehicleProfile | null, b: VehicleProfile | null) =>
  a !== null &&
  b !== null &&
  a.body === b.body &&
  a.partialLdm === b.partialLdm &&
  a.forklift === b.forklift;

export interface PostingEligibility {
  allowed: boolean;
  reason?: string;
  environment: string;
  /**
   * Czy spółka zlecenia ma komplet danych dostępowych do giełdy. Brak pola
   * (starsze API, zlecenie usunięte, spółka bez prawa do giełd) = nie wiadomo
   * — ekran nie mówi wtedy ani o braku dostępu, ani o trybie testowym.
   */
  accessConfigured?: boolean;
  /**
   * Powód braku konta innego niż brak danych spółki — konto imienne tradera
   * (16.10.2026, przełącznik `exchangePersonalAccount`). Brak = komunikat ogólny.
   */
  accessReason?: string;
  /** Z którego konta pójdzie oferta — tylko przy przełącznikach kont (16.10.2026). */
  account?: ExchangeAccountView;
  bramki?: PostingGates | null;
  /** Starsze API go nie wysyła — wtedy okno nie pyta o pojazd. */
  vehicle?: VehicleSuggestion;
  /** Warianty floty przy zleceniu w Trade — ostrzeżenie, nie blokada (fala B). */
  fleetVariants?: FleetVariantView[];
}

/** Wniosek o wyjątek od bramki publikacji — z API (`postings/publication-exceptions.ts`). */
export interface ExceptionRequestView {
  id: number;
  userOfferId: number;
  /** pending | approved | rejected | withdrawn | expired — dane. */
  status: string;
  statusLabel: string;
  rule: string;
  ruleLabel: string;
  reason: string;
  requestedBy: { id: number; username: string } | null;
  createdAt: string;
  decidedBy: { id: number; username: string } | null;
  decidedAt: string | null;
  decisionReason: string | null;
  expiredAt: string | null;
  expiryReason: string | null;
}

/** Decyzje człowieka wysyłane razem ze zleceniem publikacji. */
export interface PublishDecisions {
  reason?: string;
  eligibilityConfirmed?: boolean;
  banException?: boolean;
  maskingMode?: string;
  /** Pojazd z okna publikacji; bez niego serwer bierze typ zlecenia. */
  vehicle?: VehicleProfile;
  /** Okno pokazało ostrzeżenie o wariantach floty (fala B) — zapis, nie bramka. */
  fleetVariantsShown?: boolean;
  /** Trader potwierdził dane do publikacji (paczka p. Mariusza 21.10, S-21.10-19). */
  dataConfirmed?: boolean;
}

/**
 * Zapis potwierdzenia danych w wyniku publikacji (`dane_potwierdzone`, API
 * `publication-data.ts`) zdaniem; brak zapisu albo zły kształt — `null`.
 */
export function storedDataConfirmationText(stored: unknown): string | null {
  if (!stored || typeof stored !== 'object') return null;
  const s = stored as Record<string, unknown>;
  if (typeof s.pojazd !== 'string') return null;
  // Podpowiedź przy zleceniu bez typu to domyślny typ floty, nie „z typu
  // zlecenia” — źródło (`zrodlo`, API `describeSource`) zaczyna się wtedy
  // od „domyślnie” (integracja fali C, 22.10.2026).
  const fromDefault = typeof s.zrodlo === 'string' && s.zrodlo.startsWith('domyślnie');
  const hint = s.podpowiedz !== true ? '' : fromDefault ? ', domyślny typ floty — zlecenie bez typu pojazdu' : ', z typu zlecenia';
  return `dane do publikacji potwierdzone przy zleceniu (pojazd: ${s.pojazd}${hint})`;
}

/** Potwierdzenie danych do publikacji (S-21.10-19) — klucz z rejestru API. */
export const FEATURE_PUBLICATION_DATA_CONFIRMATION = 'publicationDataConfirmation';

/** Wysokość ładunku — TMS jej nie podaje (zdanie jak w API, `publication-data.ts`). */
export const HEIGHT_NOTE = 'brak w TMS — na giełdę idzie typ zabudowy';

/**
 * Skąd wartość w kroku potwierdzenia: podpowiedź z typu zlecenia, domyślny
 * typ floty przy zleceniu bez typu pojazdu albo wybór tradera. Przy
 * zleceniu bez typu mówimy wprost, że typu brak i podpowiadamy JUMBO —
 * „z typu zlecenia” byłoby nieprawdą (integracja fali C, 22.10.2026).
 */
const DATA_SOURCE_SUFFIX = {
  order: ' — podpowiedź z typu zlecenia, do potwierdzenia',
  default: ' — typu pojazdu w zleceniu brak; podpowiadamy domyślny typ floty (JUMBO), do sprawdzenia',
  manual: ' — wybór ręczny',
} as const;

/** Podpis pola potwierdzenia — przy domyślnym typie floty mówi to wprost. */
export function dataConfirmationLabel(suggestionSource: VehicleSuggestion['source'] | undefined): string {
  return suggestionSource === 'default'
    ? 'Potwierdzam dane do publikacji — także domyślny typ floty (JUMBO), bo zlecenie nie ma typu pojazdu'
    : 'Potwierdzam dane do publikacji — także wartości podpowiedziane z typu zlecenia';
}

/**
 * Wiersze kroku „Potwierdzam dane do publikacji” — z pojazdu w oknie
 * (podpowiedź z typu zlecenia, domyślny typ floty przy zleceniu bez typu
 * albo wybór tradera). Długość i masę pokazuje podgląd oferty obok,
 * z założeniami (np. wartość domyślna do potwierdzenia).
 */
export function publicationDataRows(
  profile: VehicleProfile | null,
  fromSuggestion: boolean,
  suggestionSource: VehicleSuggestion['source'] | undefined = 'order',
): Array<{ label: string; value: string }> {
  const source = !fromSuggestion
    ? DATA_SOURCE_SUFFIX.manual
    : suggestionSource === 'default'
      ? DATA_SOURCE_SUFFIX.default
      : DATA_SOURCE_SUFFIX.order;
  if (!profile) {
    return [{ label: 'Typ pojazdu', value: 'nie wybrano — wybierz pojazd w oknie' }];
  }
  const body = VEHICLE_BODY_OPTIONS.find((o) => o.kod === profile.body)?.label ?? profile.body;
  return [
    { label: 'Typ pojazdu i zabudowa', value: `${body}${source}` },
    {
      label: 'Długość ładunku',
      value:
        profile.partialLdm === null
          ? 'całe auto — długość (LDM) ze zlecenia albo wartość domyślna, jak w podglądzie oferty'
          : `ładunek częściowy ${String(profile.partialLdm).replace('.', ',')} LDM${source}`,
    },
    { label: 'Wysokość ładunku', value: HEIGHT_NOTE },
    { label: 'Wózek widłowy', value: `${profile.forklift ? 'wymagany' : 'niewymagany'}${source}` },
  ];
}

export interface ExchangeAccounts {
  environment: string;
  /** Trans.eu: czy wystawianie jest odblokowane w tej instancji. */
  publishingEnabled?: boolean;
  companies: Array<{
    companyId: number | null;
    nazwa: string | null;
    fields: string[];
    complete: boolean;
    missing: string[];
  }>;
}

const BASE = '/postings';

export async function fetchOrderPublications(
  userOfferId: number | string,
): Promise<{
  postings: Publication[];
  /** TIMOCOM — pole sprzed Trans.eu, zostaje dla zgodności. */
  canPublish: PostingEligibility;
  /** Wszystkie giełdy (od 24.09.2026); starsze API go nie wysyła. */
  canPublishByExchange?: Record<string, PostingEligibility>;
}> {
  const { data } = await axiosNest.get(`${BASE}/order/${userOfferId}`);
  return data;
}

export interface PublishedAddress {
  city: string;
  postalCode?: string;
}

/** Oferta w kształcie, w jakim poszłaby na giełdę — bez wysyłania. */
export interface OfferPreview {
  ok: boolean;
  reason: string | null;
  tryb: string | null;
  braki: string[];
  zalozenia?: string[];
  oferta: TimocomOfferView | TranseuOfferView | null;
  lokalizacja?: {
    tryb: string;
    zaladunek: { rzeczywista: PublishedAddress; publikowana: PublishedAddress };
    rozladunek: { rzeczywista: PublishedAddress; publikowana: PublishedAddress };
  };
}

/** Oferta TIMOCOM w podglądzie. */
export interface TimocomOfferView {
    objectType: string;
    customer: { id: number | null };
    freightDescription: string;
    length_m: number;
    weight_t: number;
    vehicleProperties: { body: string[]; type: string[] };
    loadingPlaces: Array<{
      loadingType: 'LOADING' | 'UNLOADING';
      address: {
        city: string;
        country: string;
        postalCode?: string;
      };
      earliestLoadingDate: string;
      latestLoadingDate: string;
      startTime?: string;
      endTime?: string;
    }>;
}

/** Oferta Trans.eu w podglądzie — punkty z oknami czasu, masa i ldm. */
export interface TranseuOfferView {
  capacity: number;
  loading_meters: number;
  spots: Array<{
    spot_order: number;
    place: { address: { country: string; postal_code?: string; locality: string } };
    operations: Array<{
      type: 'loading' | 'unloading';
      timespans: { begin: string; end: string };
    }>;
  }>;
}

/** Dzień `RRRR-MM-DD` jako „22.09.2026”; inny tekst zostaje, jak przyszedł. */
const dayText = (day?: string | null): string =>
  day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? calendarDate(day) : (day ?? '');

/** Punkt trasy w podglądzie — wspólny opis dla obu giełd. */
export interface PreviewStop {
  address: PublishedAddress;
  when: string;
}

/**
 * Oferta dowolnej giełdy sprowadzona do tego, co podgląd pokazuje: skąd, dokąd,
 * kiedy i co. Kształt każdej giełdy zna tylko ta funkcja.
 */
export function describeOffer(
  offer: TimocomOfferView | TranseuOfferView | null,
): { loading: PreviewStop | null; unloading: PreviewStop | null; cargo: string } | null {
  if (!offer) return null;
  if ('spots' in offer) {
    const stop = (type: 'loading' | 'unloading'): PreviewStop | null => {
      const spot = offer.spots.find((s) => s.operations.some((o) => o.type === type));
      if (!spot) return null;
      const op = spot.operations.find((o) => o.type === type);
      const a = spot.place.address;
      // Okna Trans.eu idą w prawdziwym UTC (`…Z`) — pokazujemy je w czasie
      // polskim, jak resztę terminów. Wycinek tekstu dawał operatorowi
      // godzinę UTC w zapisie „2026-10-01 06:00” (30.09.2026).
      const span = op ? dateTimeRange(op.timespans.begin, op.timespans.end) : '';
      return {
        address: a.postal_code ? { city: a.locality, postalCode: a.postal_code } : { city: a.locality },
        when: span,
      };
    };
    return {
      loading: stop('loading'),
      unloading: stop('unloading'),
      // Liczby po polsku i LDM wielkimi literami, jak w reszcie ekranów (01.10.2026).
      cargo: `${plNumber(offer.capacity, 3)} t · ${plNumber(offer.loading_meters, 2)} LDM`,
    };
  }
  const loading = offer.loadingPlaces?.find((p) => p.loadingType === 'LOADING');
  const unloading = offer.loadingPlaces?.find((p) => p.loadingType === 'UNLOADING');
  return {
    // TIMOCOM dostaje sam dzień (`RRRR-MM-DD`, już polski) — na ekranie jak
    // każda data: „22.09.2026”.
    loading: loading ? { address: loading.address, when: dayText(loading.earliestLoadingDate) } : null,
    unloading: unloading
      ? { address: unloading.address, when: dayText(unloading.latestLoadingDate) }
      : null,
    cargo: `${offer.freightDescription} · ${plNumber(offer.weight_t, 3)} t · ${plNumber(offer.length_m, 2)} LDM`,
  };
}

/**
 * Co pojedzie na giełdę — pytamy serwer, zamiast składać ofertę po swojemu.
 * Podgląd zbudowany osobnym kodem rozjechałby się z wysyłką przy pierwszej
 * zmianie reguł i byłby gorszy niż jego brak.
 */
export async function fetchOfferPreview(
  userOfferId: number | string,
  maskingMode?: string,
  exchange: string = EXCHANGE_TIMOCOM,
  vehicle?: VehicleProfile,
): Promise<OfferPreview> {
  const params = {
    ...(maskingMode ? { maskingMode: maskingMode } : {}),
    // TIMOCOM jest domyślny po stronie API — parametr tylko dla innych giełd.
    ...(exchange !== EXCHANGE_TIMOCOM ? { exchange: exchange } : {}),
    // Podgląd pokazuje pojazd wybrany w oknie, nie podpowiedź.
    ...(vehicle
      ? {
          vehicleBody: vehicle.body,
          ...(vehicle.partialLdm !== null ? { partialLdm: String(vehicle.partialLdm) } : {}),
          ...(vehicle.forklift ? { forklift: 'true' } : {}),
        }
      : {}),
  };
  const { data } = await axiosNest.get(
    `${BASE}/order/${userOfferId}/preview`,
    Object.keys(params).length > 0 ? { params } : undefined,
  );
  return data;
}

export async function requestPublication(
  userOfferId: number | string,
  exchange: string = EXCHANGE_TIMOCOM,
  decisions: PublishDecisions = {},
): Promise<Publication> {
  const { data } = await axiosNest.post(`${BASE}/order/${userOfferId}`, {
    exchange: exchange,
    reason: decisions.reason || undefined,
    eligibilityConfirmed: decisions.eligibilityConfirmed === true,
    banException: decisions.banException === true,
    maskingMode: decisions.maskingMode || undefined,
    ...(decisions.vehicle ? { vehicle: decisions.vehicle } : {}),
    ...(decisions.fleetVariantsShown ? { fleetVariantsShown: true } : {}),
    // Bez przełącznika klucza nie wysyłamy — API sprzed zmiany go nie zna.
    ...(decisions.dataConfirmed !== undefined ? { dataConfirmed: decisions.dataConfirmed } : {}),
  });
  return data;
}

export interface CompanyCell {
  id: number;
  name: string;
  country: string | null;
  locationMasking: string | null;
  /** Konto giełdowe wspólne (16.10.2026) — starsze API go nie wysyła. */
  exchangeAccountCompanyId?: number | null;
}

export async function fetchCompanies(): Promise<CompanyCell[]> {
  const { data } = await axiosNest.get('/companies');
  return data;
}

export async function setCompanyMasking(
  companyId: number,
  mode: string | null,
): Promise<CompanyCell> {
  const { data } = await axiosNest.put(`/companies/${companyId}/location-masking`, { tryb: mode });
  return data;
}

export async function setSubcontractorRule(
  contractorId: number,
  rule: string | null,
  notes?: string,
): Promise<unknown> {
  const { data } = await axiosNest.put(`/contractors/${contractorId}/subcontracting`, {
    regula: rule,
    uwagi: notes || undefined,
  });
  return data;
}

export async function withdrawPublication(
  id: number,
  options: { force?: boolean; reason?: string } = {},
): Promise<Publication> {
  const { data } = await axiosNest.post(`${BASE}/${id}/withdraw`, {
    force: options.force === true,
    reason: options.reason || undefined,
  });
  return data;
}

export interface TranseuLoginCheck {
  companyId: number;
  ok: boolean;
  error: string | null;
  /** Szczegóły techniczne odmowy; starsze API ich nie wysyła. */
  errorDetails?: string | null;
  publishingEnabled: boolean;
}

/** Sprawdzenie logowania Trans.eu spółki — tylko token, nic nie publikuje. */
export async function checkTranseuLogin(companyId: number): Promise<TranseuLoginCheck> {
  const { data } = await axiosNest.post(`${BASE}/accounts/transeu/check`, null, {
    params: { companyId },
  });
  return data;
}

export async function retryPublication(id: number): Promise<Publication> {
  const { data } = await axiosNest.post(`${BASE}/${id}/retry`);
  return data;
}

export async function cancelPublication(id: number): Promise<Publication> {
  const { data } = await axiosNest.delete(`${BASE}/${id}`);
  return data;
}

export interface QueueFilters {
  status?: string;
  exchange?: string;
  q?: string;
}

export async function fetchQueue(
  filters: QueueFilters,
  page: { start: number; end: number },
): Promise<{ rows: Publication[]; total: number }> {
  const cleaned = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== undefined && v !== ''),
  );
  const response = await axiosNest.get(BASE, {
    params: {
      range: JSON.stringify([page.start, page.end]),
      sort: JSON.stringify(['requestedAt', 'DESC']),
      filter: JSON.stringify(cleaned),
    },
  });
  const header: string | undefined = response.headers?.['content-range'];
  const total = header ? Number(header.split('/')[1]) : (response.data as Publication[]).length;
  return { rows: response.data, total: Number.isFinite(total) ? total : 0 };
}

export async function fetchAccounts(
  exchange: string = EXCHANGE_TIMOCOM,
): Promise<ExchangeAccounts> {
  const { data } =
    exchange === EXCHANGE_TIMOCOM
      ? await axiosNest.get(`${BASE}/accounts`)
      : await axiosNest.get(`${BASE}/accounts`, { params: { exchange: exchange } });
  return data;
}

export async function processQueue(): Promise<
  Array<{ id: number; status: string; details: string }>
> {
  const { data } = await axiosNest.post(`${BASE}/process`);
  return data;
}

/** Czy publikacja „żyje" — ma sens pokazywać przy niej wycofanie / anulowanie. */
export const isLive = (p: Publication) =>
  p.status === STATUS.PENDING || p.status === STATUS.POSTED;
